import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ViewportScroller } from '@angular/common';
import { AfterViewInit, Component, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { KeenSliderComponent } from 'src/app/components/keen-slider/keen-slider.component';
import { FileDropzoneComponent } from '../../components/file-dropzone/file-dropzone.component';
import { ProfileComponent } from '../profile/profile.component';

/**
 * Interface for the example Table
 * @interface PeriodicElement
 * @typedef {PeriodicElement}
 */
export interface PeriodicElement {
    name: string;
    email: string;
    avatar: string;
    text: string;
    link: string;
    status: boolean;
    button: string;
}

/**
 * Define the Runtimes interface
 * @interface Runtimes
 * @typedef {Runtimes}
 */
export interface Runtimes {
    os: string;
    description: string;
    icon: string;
    url: string;
}

/**
 * Elements for the example table
 * @type {PeriodicElement[]}
 */
const ELEMENT_DATA: PeriodicElement[] = [
    {
        avatar: 'scope-bubbly-rabbit',
        name: 'Hydrogen',
        email: '007@example.bond',
        text: '1.0079',
        link: 'Link',
        status: true,
        button: 'H'
    },
    {
        avatar: 'scope-bumble-bee',
        name: 'Helium',
        email: '007@example.bond',
        text: '4.0026',
        link: 'Link',
        status: false,
        button: 'He'
    },
    {
        avatar: 'scope-celebrating-specialist',
        name: 'Lithium',
        email: '007@example.bond',
        text: '6.941',
        link: 'Link',
        status: true,
        button: 'Li'
    },
    {
        avatar: 'scope-clever-nerd',
        name: 'Beryllium',
        email: '007@example.bond',
        text: '9.0122',
        link: 'Link',
        status: false,
        button: 'Be'
    },
    {
        avatar: 'scope-cool-specialist',
        name: 'Boron',
        email: '007@example.bond',
        text: '10.811',
        link: 'Link',
        status: true,
        button: 'B'
    },
    {
        avatar: 'scope-heart',
        name: 'Carbon',
        email: '007@example.bond',
        text: '12.0107',
        link: 'Link',
        status: true,
        button: 'C'
    },
    {
        avatar: 'scope-crying-specialist',
        name: 'Nitrogen',
        email: '007@example.bond',
        text: '14.0067',
        link: 'Link',
        status: true,
        button: 'N'
    },
    {
        avatar: 'scope-cuddly-cat',
        name: 'Oxygen',
        email: '007@example.bond',
        text: '15.9994',
        link: 'Link',
        status: false,
        button: 'O'
    },
    {
        avatar: 'scope-devoted-dog',
        name: 'Fluorine',
        email: '007@example.bond',
        text: '18.9984',
        link: 'Link',
        status: true,
        button: 'F'
    },
    {
        avatar: 'scope-happy-specialist',
        name: 'Neon',
        email: '007@example.bond',
        text: '20.1797',
        link: 'Link',
        status: true,
        button: 'Ne'
    }
];

/**
 * Styleguide component
 * @class StyleGuideComponent
 * @typedef {StyleGuideComponent}
 * @implements {AfterViewInit}
 */
@Component( {
    selector: 'app-styleguide',
    templateUrl: './styleguide.component.html',
    styleUrl: './styleguide.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: false
} )
export class StyleGuideComponent implements AfterViewInit {
    search = new FormControl( '' );
    inputFormControl = new FormControl( '', [Validators.required] );

    hasFile: BehaviorSubject<boolean> = new BehaviorSubject( false );
    signature?: string;

    displayedColumns: string[] = ['avatar', 'name', 'text', 'link', 'status', 'button'];
    tableDataSource = new MatTableDataSource( ELEMENT_DATA );

    services = [
        { id: 'airfreight', icon: 'flight', text: $localize`:@@button-label-airfreight:Airfreight` },
        { id: 'seafreight', icon: 'directions_boat', text: $localize`:@@button-label-seafreight:Seafreight` },
        { id: 'customs', icon: 'account_balance', text: $localize`:@@button-label-customs:Customs` },
        { id: 'express', icon: 'schedule', text: $localize`:@@button-label-express:Express` },
        { id: 'export', icon: 'logout', text: $localize`:@@button-label-export:Export` },
        { id: 'import', icon: 'login', text: $localize`:@@button-label-import:Import` }
    ];

    communityData = [
        {
            title: $localize`:@@scope-community-title-1:Scope Community 1`,
            address: {
                street: 'Frachtgebäude Modul G',
                zip: '85356',
                city: $localize`:@@scope-community-city-munich:Munich Airport`,
                country: $localize`:@@scope-community-country-germany:Germany`
            },
            services: this.services.filter((s) => ['airfreight', 'seafreight'].includes(s.id))
        },
        {
            title: $localize`:@@scope-community-title-2:Scope Community 2`,
            address: {
                street: 'Example Street 10',
                zip: '10115',
                city: $localize`:@@scope-community-city-berlin:Berlin`,
                country: $localize`:@@scope-community-country-germany:Germany`
            },
            services: this.services.filter((s) => ['import', 'export', 'customs'].includes(s.id))
        },
        {
            title: $localize`:@@scope-community-title-3:Scope Community 3 with LINK`,
            address: {
                street: 'Frachtgebäude Modul G',
                zip: '85356',
                city: $localize`:@@scope-community-city-munich:Munich Airport`,
                country: $localize`:@@scope-community-country-germany:Germany`
            },
            services: this.services.filter((s) => ['airfreight', 'seafreight'].includes(s.id)),
            link: 'https://example.com/community1'
        },
        {
            title: $localize`:@@scope-community-title-4:Scope Community 4`,
            address: {
                street: 'Example Street 10',
                zip: '10115',
                city: $localize`:@@scope-community-city-berlin:Berlin`,
                country: $localize`:@@scope-community-country-germany:Germany`
            },
            services: this.services.filter((s) => ['import', 'export', 'customs'].includes(s.id))
        },
        {
            title: $localize`:@@scope-community-title-5:Scope Community 5`,
            address: {
                street: 'Example Street 10',
                zip: '10115',
                city: $localize`:@@scope-community-city-cologne:Cologne`,
                country: $localize`:@@scope-community-country-germany:Germany`
            },
            services: this.services.filter((s) => ['import', 'export', 'customs'].includes(s.id))
        },
        {
            title: $localize`:@@scope-community-title-6:Scope Community 6`,
            address: {
                street: 'Example Street 10',
                zip: '10115',
                city: $localize`:@@scope-community-city-mannheim:mannheim`,
                country: $localize`:@@scope-community-country-germany:Germany`
            },
            services: this.services.filter((s) => ['import', 'export', 'customs'].includes(s.id))
        }
    ];

    /**
     * Runtimes
     * @type {Runtimes[]}
     */
    RUNTIMES: Runtimes[] = [
        {
            os: 'Windows 64-bit',
            description: '',
            icon: 'windows',
            url: '#'
        },
        {
            os: 'macOS',
            description: '',
            icon: 'macos',
            url: '#'
        },
        {
            os: 'Linux',
            description: '',
            icon: 'windows',
            url: '#'
        }
    ];

    @ViewChild( FileDropzoneComponent ) fileDropzoneComponent!: FileDropzoneComponent;
    @ViewChild( ProfileComponent ) profileComponent!: ProfileComponent;
    @ViewChild( MatSort ) sort!: MatSort;
    @ViewChild( 'dialogTemplate' ) dialogTemplate!: TemplateRef<any>;
    @ViewChild( 'sidenavTemplate' ) sidenavTemplate!: TemplateRef<any>;
    @ViewChild(KeenSliderComponent) KeenSliderComponent!: KeenSliderComponent;

    constructor(
        private _liveAnnouncer: LiveAnnouncer,
        private _snackBar: MatSnackBar,
        public dialog: MatDialog,
        private viewportScroller: ViewportScroller,
        private route: ActivatedRoute,
        private router: Router
    ) {
        // Set scroll offset for fixed toolbar (e.g., 80px)
        this.viewportScroller.setOffset( [0, 80] );
    }

    /**
     * After view Init statements
     */
    ngAfterViewInit() {
        this.tableDataSource.sort = this.sort;
        // Subscribe to route fragment changes
        this.route.fragment.subscribe( ( fragment ) => {
            if ( fragment ) {
                this.viewportScroller.scrollToAnchor( fragment );
            }
        } );
    }

    private scrollToFragment( fragment: string ): void {
        const element = document.getElementById( fragment );
        if ( element ) {
            const offset = 80; // Adjust for your fixed toolbar height
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - offset;

            // Smooth scroll to the position
            window.scrollTo( {
                top: offsetPosition,
                behavior: 'smooth'
            } );
        }
    }

    /**
     * Announce the change in sort state for assistive technology.
     * @param {Sort} sortState
     */
    announceSortChange( sortState: Sort ) {
        if ( sortState.direction ) {
            this._liveAnnouncer.announce( `Sorted ${sortState.direction}ending` );
        } else {
            this._liveAnnouncer.announce( 'Sorting cleared' );
        }
    }

    /**
     * get Dropzone File Function
     * @param {unknown} uploadedFile
     */
    getDropzoneFile( uploadedFile: unknown ) {
        this.signature = uploadedFile as string;
        this.hasFile.next( true );
    }

    /**
     * Upload Dropzone File Function
     * @param {File[]} $files
     */
    UploadDropzoneFile( $files: File[] ): void {
        this.getDropzoneFile( $files );
    }

    /**
     * Delete Signatrue function
     */
    deleteSignature() {
        this.hasFile.next( false );
    }

    /**
     * Open Snackbar function
     * @param {string} message
     * @param {string} action
     */
    openSnackBar( message: string, action: string ) {
        this._snackBar.open( message, action, { duration: 100000 } );
    }

    /**
     * Open Dialog function
     */
    openDialog() {
        this.dialog.open( this.dialogTemplate, { panelClass: 'riege-dialog' } );
    }

    /**
     * Open Styleguide Navigation Sidebar Panel function
     */
    toggleStyleguideNavigation() {
        this.dialog.open( this.sidenavTemplate, { panelClass: ['sidepanel', 'sidepanel--fade-in-right'] } );
    }

    navigateTo( fragment: string ): void {
        this.dialog.closeAll(); // Close the dialog
        setTimeout( () => {
            // Use router to navigate and scroll to the fragment
            this.router.navigate( ['/styleguide'], { fragment } );
        }, 200 ); // Delay to ensure dialog closes first
    }
}
